import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Brush, ImagePlus, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (path) => `${API}/api/media/files/${path}`;

export function FanArtSection() {
  const [featured, setFeatured] = useState([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/media/featured-fan-art`).then((res) => res.json()).then(setFeatured).catch(() => {});
  }, [status === "done"]);

  const submit = async (event) => {
    event.preventDefault();
    if (!file) return;
    setStatus("sending");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("name", name);
      body.append("message", message);
      const response = await fetch(`${API}/api/media/fan-art`, { method: "POST", body });
      if (!response.ok) throw new Error("upload failed");
      setStatus("done");
      setName("");
      setMessage("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="fan-art" className="ns-section ns-fanart" data-testid="fanart-section">
      <motion.div className="ns-fanart-inner" initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
        <div className="ns-fanart-copy">
          <p className="ns-kicker"><Brush size={14} /> Little artists&rsquo; corner</p>
          <h2>Your art,<br /><em>in our world.</em></h2>
          <p>Draw Niyati, Shivu, Buddy or your favourite magical world — the loveliest creations get featured right here for every family to see.</p>
          <form className="ns-fanart-form" data-testid="fanart-form" onSubmit={submit}>
            <input type="text" data-testid="fanart-name-input" placeholder="Artist's first name" value={name} onChange={(event) => setName(event.target.value)} maxLength={60} required />
            <input type="text" data-testid="fanart-message-input" placeholder="Tell us about your drawing…" value={message} onChange={(event) => setMessage(event.target.value)} maxLength={280} />
            <label className="ns-fanart-file" data-testid="fanart-file-label">
              <ImagePlus size={16} /> {file ? file.name : "Choose your drawing"}
              <input ref={fileRef} type="file" data-testid="fanart-file-input" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(event) => setFile(event.target.files?.[0] || null)} required hidden />
            </label>
            {status === "error" && <p className="ns-form-error" data-testid="fanart-error">We could not save your drawing just now. Please try a JPG, PNG, WEBP or GIF under 8 MB.</p>}
            <button className="ns-button ns-button-primary" data-testid="fanart-submit-button" disabled={status === "sending" || !file}>{status === "sending" ? "Sending…" : <>Share my artwork <Send size={15} /></>}</button>
            {status === "done" && <p className="ns-form-success" data-testid="fanart-success">Thank you, little artist! A grown-up will feature it soon.</p>}
          </form>
        </div>
        {featured.length > 0 && (
          <div className="ns-fanart-gallery" data-testid="fanart-gallery">
            {featured.map((art) => (
              <figure key={art.id} data-testid={`fanart-item-${art.id}`}>
                <img src={fileUrl(art.storage_path)} alt={art.title || `Fan art by ${art.name}`} loading="lazy" />
                <figcaption><strong>{art.name || "Little artist"}</strong>{art.message && <span>{art.message}</span>}</figcaption>
              </figure>
            ))}
          </div>
        )}
      </motion.div>
    </section>
  );
}
